/**
 * Simulation engine for the CHE 334 Virtual Pumping System Lab.
 *
 * Pure functions — no Vue dependencies.
 * All units are SI unless noted:
 *   Length: m        Velocity: m/s      Flow rate: m³/s
 *   Pressure head: m of fluid           Density: kg/m³
 *   Viscosity: Pa·s                     Power: W
 */

import { M_TO_FT, W_TO_HP, M3S_TO_GPM, RE_LAMINAR } from "@/utils/units.js";

const G = 9.81; // gravitational acceleration (m/s²)

// ─── Fundamental calculations ────────────────────────────────────────

/**
 * Cross-sectional area of a circular pipe.
 * @param {number} D – internal diameter (m)
 * @returns {number} area (m²)
 */
export function pipeArea(D) {
  return (Math.PI / 4) * D * D;
}

/**
 * Average fluid velocity from volumetric flow rate and pipe diameter.
 * @param {number} Q – volumetric flow rate (m³/s)
 * @param {number} D – internal diameter (m)
 * @returns {number} velocity (m/s)
 */
export function velocity(Q, D) {
  return Q / pipeArea(D);
}

/**
 * Reynolds number.
 * @param {number} rho – fluid density (kg/m³)
 * @param {number} v   – average velocity (m/s)
 * @param {number} D   – internal diameter (m)
 * @param {number} mu  – dynamic viscosity (Pa·s)
 * @returns {number}
 */
export function reynoldsNumber(rho, v, D, mu) {
  return (rho * v * D) / mu;
}

// ─── Friction factor ─────────────────────────────────────────────────

/**
 * Darcy friction factor.
 *   Laminar (Re < 2300):  f = 64 / Re
 *   Turbulent:            Swamee–Jain explicit approximation
 *
 * @param {number} Re  – Reynolds number
 * @param {number} eps – absolute roughness ε (m)
 * @param {number} D   – internal diameter (m)
 * @returns {number} Darcy friction factor (dimensionless)
 */
export function frictionFactor(Re, eps, D) {
  if (Re <= 0) return 0;
  if (Re < RE_LAMINAR) {
    return 64 / Re;
  }
  // Swamee–Jain (valid for 5×10³ < Re < 10⁸ and 10⁻⁶ < ε/D < 10⁻²)
  const relRoughness = eps / D;
  const denom = Math.log10(relRoughness / 3.7 + 5.74 / Math.pow(Re, 0.9));
  return 0.25 / (denom * denom);
}

// ─── Head losses ─────────────────────────────────────────────────────

/**
 * Major (friction) head loss — Darcy–Weisbach equation.
 * hf = f · (L / D) · (v² / 2g)
 *
 * @param {number} f – Darcy friction factor
 * @param {number} L – pipe length (m)
 * @param {number} D – internal diameter (m)
 * @param {number} v – average velocity (m/s)
 * @returns {number} head loss (m)
 */
export function majorHeadLoss(f, L, D, v) {
  return (f * (L / D) * (v * v)) / (2 * G);
}

/**
 * Minor head loss from fittings & valves.
 * hm = Ktotal · (v² / 2g)
 *
 * @param {number} Ktotal – sum of all minor-loss coefficients
 * @param {number} v      – average velocity (m/s)
 * @returns {number} head loss (m)
 */
export function minorHeadLoss(Ktotal, v) {
  return (Ktotal * (v * v)) / (2 * G);
}

// ─── System head curve ───────────────────────────────────────────────

/**
 * Compute the total system head at a given flow rate Q.
 * Hsys(Q) = Δz + hf(Q) + hm(Q)
 *
 * @param {object} params
 * @param {number} params.Q    – volumetric flow rate (m³/s)
 * @param {number} params.dz   – elevation change (m)
 * @param {number} params.L    – total pipe length (m)
 * @param {number} params.D    – internal diameter (m)
 * @param {number} params.eps  – pipe roughness ε (m)
 * @param {number} params.rho  – fluid density (kg/m³)
 * @param {number} params.mu   – fluid dynamic viscosity (Pa·s)
 * @param {number} params.Ktotal – sum of minor-loss K values
 * @returns {number} system head (m)
 */
export function systemHead({ Q, dz, L, D, eps, rho, mu, Ktotal }) {
  if (Q <= 0) return dz;
  const v = velocity(Q, D);
  const Re = reynoldsNumber(rho, v, D, mu);
  const f = frictionFactor(Re, eps, D);
  const hf = majorHeadLoss(f, L, D, v);
  const hm = minorHeadLoss(Ktotal, v);
  return dz + hf + hm;
}

// ─── Pump head curve ─────────────────────────────────────────────────

/**
 * Pump head from the quadratic pump curve.
 * Hp(Q) = a − b·Q²
 *
 * @param {number} Q – volumetric flow rate (m³/s)
 * @param {number} a – shutoff head (m)
 * @param {number} b – curve coefficient (m/(m³/s)²)
 * @returns {number} pump head (m)
 */
export function pumpHead(Q, a, b) {
  return a - b * Q * Q;
}

// ─── Operating-point solver ──────────────────────────────────────────

/**
 * Find the operating point Q* where Hp(Q) = Hsys(Q) using bisection.
 *
 * Returns null if no intersection exists (pump cannot overcome system head).
 *
 * @param {object} sysParams – all params for systemHead (excluding Q)
 * @param {number} a  – pump curve coefficient a
 * @param {number} b  – pump curve coefficient b
 * @param {number} [maxQ] – upper bound for search (m³/s), defaults to sqrt(a/b)
 * @param {number} [tol=1e-8]  – convergence tolerance (m³/s)
 * @param {number} [maxIter=100]
 * @returns {{ Q: number, Hp: number, Hsys: number } | null}
 */
export function findOperatingPoint(
  sysParams,
  a,
  b,
  maxQ,
  tol = 1e-8,
  maxIter = 100
) {
  // Upper bound: flow where pump head drops to zero
  const Qmax = maxQ || Math.sqrt(a / b);

  // f(Q) = Hp(Q) - Hsys(Q);  we seek the root where f(Q) = 0
  const f = (Q) => pumpHead(Q, a, b) - systemHead({ ...sysParams, Q });

  // Check: at Q = 0, pump head must exceed static head
  const f0 = f(0);
  if (f0 < 0) return null; // pump can't even overcome Δz

  // Check: at Q = Qmax, system head should exceed pump head
  const fMax = f(Qmax);
  if (fMax > 0) {
    // Pump always dominates in range — operate at Qmax (unusual but valid)
    const Hp = pumpHead(Qmax, a, b);
    return { Q: Qmax, Hp, Hsys: systemHead({ ...sysParams, Q: Qmax }) };
  }

  // Bisection
  let lo = 0;
  let hi = Qmax;
  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const fm = f(mid);
    if (Math.abs(fm) < tol || (hi - lo) / 2 < tol) {
      const Qstar = mid;
      return {
        Q: Qstar,
        Hp: pumpHead(Qstar, a, b),
        Hsys: systemHead({ ...sysParams, Q: Qstar })
      };
    }
    if (fm > 0) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  // Didn't converge — return best estimate
  const Qstar = (lo + hi) / 2;
  return {
    Q: Qstar,
    Hp: pumpHead(Qstar, a, b),
    Hsys: systemHead({ ...sysParams, Q: Qstar })
  };
}

// ─── Power & efficiency ──────────────────────────────────────────────

/**
 * Hydraulic power delivered to the fluid.
 * Phyd = ρ · g · Q · H
 *
 * @param {number} rho – fluid density (kg/m³)
 * @param {number} Q   – flow rate (m³/s)
 * @param {number} H   – head (m)
 * @returns {number} hydraulic power (W)
 */
export function hydraulicPower(rho, Q, H) {
  return rho * G * Q * H;
}

/**
 * Electrical power consumed by the pump.
 * Pelec = Phyd / η
 *
 * @param {number} Phyd – hydraulic power (W)
 * @param {number} eta  – pump efficiency (0–1)
 * @returns {number} electrical power (W)
 */
export function electricalPower(Phyd, eta) {
  if (eta <= 0) return Infinity;
  return Phyd / eta;
}

/**
 * Estimate pump efficiency at the operating flow rate.
 * Simple parabolic model centred on peakQ:
 *   η(Q) = peakEta · (1 − ((Q − peakQ) / peakQ)²)
 * Clamped to [0, peakEta].
 *
 * @param {number} Q       – operating flow rate (m³/s)
 * @param {number} peakEta – best efficiency (0–1)
 * @param {number} peakQ   – flow rate at best efficiency (m³/s)
 * @returns {number} efficiency (0–1)
 */
export function pumpEfficiency(Q, peakEta, peakQ) {
  if (peakQ <= 0) return 0;
  const ratio = (Q - peakQ) / peakQ;
  const eta = peakEta * (1 - ratio * ratio);
  return Math.max(0, Math.min(peakEta, eta));
}

// ─── High-level evaluation ───────────────────────────────────────────

/**
 * Run the full simulation for a student design.
 *
 * @param {object} params
 * @param {object} params.fluid     – { density, viscosity } from fluids.js
 * @param {object} params.scenario  – scenario object from scenarios.json
 * @param {object} params.pipe      – selected pipe catalog entry (with roughness, selected size object)
 * @param {number} params.Ktotal    – sum of K values from placed fittings & valves
 * @param {object} params.pump      – pump catalog entry (with curve.a, curve.b, efficiency.*)
 * @returns {object} simulation results
 */
export function evaluateDesign({ fluid, scenario, pipe, Ktotal, pump }) {
  const D = pipe.selectedSize.innerDiameter_m;
  const eps = pipe.roughness;
  const L = scenario.pipeRunLength_m;
  const dz = scenario.elevationChange_m;
  const rho = fluid.density;
  const mu = fluid.viscosity;

  const sysParams = { dz, L, D, eps, rho, mu, Ktotal };

  const { a, b } = pump.curve;
  const opPoint = findOperatingPoint(sysParams, a, b);

  if (!opPoint) {
    return {
      success: false,
      reason: "Pump cannot overcome system head — no operating point found.",
      operatingPoint: null,
      intermediates: { D, eps, L, dz, Ktotal }
    };
  }

  const { Q, Hp } = opPoint;
  const v = velocity(Q, D);
  const Re = reynoldsNumber(rho, v, D, mu);
  const f = frictionFactor(Re, eps, D);
  const hf = majorHeadLoss(f, L, D, v);
  const hm = minorHeadLoss(Ktotal, v);
  const eta = pumpEfficiency(
    Q,
    pump.efficiency.peakEta,
    pump.efficiency.peakQ_m3s
  );
  const Phyd = hydraulicPower(rho, Q, Hp);
  const Pelec = electricalPower(Phyd, eta);

  // Convert Q to GPM for display
  const Q_GPM = Q * M3S_TO_GPM;

  return {
    success: true,
    operatingPoint: {
      Q_m3s: Q,
      Q_GPM,
      Hp_m: Hp,
      Hp_ft: Hp * M_TO_FT,
      velocity_ms: v,
      velocity_fps: v * M_TO_FT
    },
    losses: {
      majorHeadLoss_m: hf,
      majorHeadLoss_ft: hf * M_TO_FT,
      minorHeadLoss_m: hm,
      minorHeadLoss_ft: hm * M_TO_FT,
      totalSystemHead_m: dz + hf + hm,
      totalSystemHead_ft: (dz + hf + hm) * M_TO_FT
    },
    power: {
      hydraulic_W: Phyd,
      hydraulic_hp: Phyd * W_TO_HP,
      electrical_W: Pelec,
      electrical_hp: Pelec * W_TO_HP,
      efficiency: eta
    },
    intermediates: {
      D,
      eps,
      L,
      dz,
      Ktotal,
      Re,
      frictionFactor: f
    }
  };
}
